/**
 * Design schema migrations (spec: CLOUD_SYNC_SPEC §9.2)
 *
 * Each MIGRATIONS[n] upgrades a design payload from schema n to n+1 as a
 * PURE JSON transform - no app, geometry or DOM dependencies, so this module
 * is importable from node tests and (in principle) a batch-migration Worker.
 *
 * Rules (§9.3):
 * - NEVER mutate a shipped migrator; only append new ones.
 * - Every migrator is covered by a frozen fixture test
 *   (tests/fixtures/design-v1.json, ...).
 * - Document each bump in docs/SCHEMA_HISTORY.md.
 */
import { DESIGN_SCHEMA_VERSION } from './version.js';

/** Thrown when a payload was written by a NEWER app than this runtime */
export class SchemaTooNewError extends Error {
    constructor(version) {
        super(`This design was made with a newer version of SketchChair ` +
            `(schema v${version}, this app reads up to v${DESIGN_SCHEMA_VERSION}). ` +
            `Please refresh or update the app.`);
        this.name = 'SchemaTooNewError';
        this.schemaVersion = version;
    }
}

// SketchSpline.TYPE_LEG - duplicated here (not imported) so migrators stay
// dependency-free. This value is frozen in every stored v1/v2 blob anyway.
const TYPE_LEG = 43;

let __migIdCounter = 0;
/** Fresh document-unique stroke id (mirrors StrokeStore.generateStrokeId) */
function migrationStrokeId() {
    __migIdCounter++;
    return `sk-mig${__migIdCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Synthesize a v2 `strokes` payload from a v1-style single `sketch` section:
 * one master per stroke, linked on every layer (outer layers only for legs) -
 * exactly what the v1 runtime's stamping produced; v1 files carry no
 * per-layer divergence to promote. Fresh strokeIds are always assigned
 * (matching the historical v1 loader, which regenerated ids on load).
 *
 * Exported so loadDesign can also repair v2-versioned files that carry a
 * legacy sketch section but no strokes payload.
 *
 * @param {Object} data - design payload with a `sketch` section
 * @returns {Object} a `strokes` payload ({chairCurve, masters, layers})
 */
export function strokesFromLegacySketch(data) {
    const sketch = data.sketch || {};
    const layerCount = Math.max(1, data.settings?.numBasePlanes || 5);

    // Masters in draw order: splines first, then paths (the order the v1
    // loader fed replaceLayerStrokes)
    const entries = [];
    for (const s of sketch.splines || []) {
        const id = migrationStrokeId();
        entries.push({
            master: { id, kind: 'spline', data: { ...s, strokeId: id } },
            leg: s.splineType === TYPE_LEG
        });
    }
    for (const p of sketch.paths || []) {
        const id = migrationStrokeId();
        entries.push({
            master: { id, kind: 'path', data: { ...p, strokeId: id } },
            leg: false
        });
    }

    const layers = [];
    for (let i = 0; i < layerCount; i++) {
        const outer = i === 0 || i === layerCount - 1;
        layers.push(entries
            .filter(e => outer || !e.leg)
            .map(e => ({ id: e.master.id, linked: true })));
    }

    return {
        chairCurve: null, // identity - v1 predates the chair curve
        masters: entries.map(e => e.master),
        layers
    };
}

/**
 * Migrator chain: MIGRATIONS[n] takes a payload at schema n and returns a
 * payload at schema n+1. Pure JSON in, pure JSON out.
 */
export const MIGRATIONS = {
    /**
     * v1 -> v2 (LINKED MASTER STROKES): promote the single stored sketch to
     * document-level stroke masters + per-layer linked instance tables.
     * The legacy `sketch` section is kept alongside (v2 writers keep it too,
     * so older builds can still open the file).
     */
    1: (data) => ({
        ...data,
        version: 2,
        strokes: strokesFromLegacySketch(data)
    })
    // 2: (data) => ...  add when DESIGN_SCHEMA_VERSION becomes 3
};

/**
 * Upgrade a design payload to DESIGN_SCHEMA_VERSION, applying migrators
 * sequentially. Run on EVERY read path regardless of source (local file,
 * autosave, cloud version, published snapshot). Does not mutate the input.
 *
 * @param {Object} data - raw parsed design payload
 * @returns {Object} payload at DESIGN_SCHEMA_VERSION
 * @throws {SchemaTooNewError} when the payload is from a newer app
 * @throws {Error} when a migrator is missing (corrupt version field)
 */
export function migrateToLatest(data) {
    let v = data.version || 1;
    if (v > DESIGN_SCHEMA_VERSION) throw new SchemaTooNewError(v);
    let out = data;
    while (v < DESIGN_SCHEMA_VERSION) {
        const migrate = MIGRATIONS[v];
        if (!migrate) {
            throw new Error(`No migrator for design schema v${v}`);
        }
        out = migrate(out);
        v++;
        if ((out.version || 1) !== v) {
            throw new Error(`Migrator ${v - 1} produced schema ` +
                `v${out.version} (expected v${v})`);
        }
    }
    return out;
}
